import React, { useEffect, useMemo, useRef, useState } from 'react';

interface FilterPanelProps {
  filters: {
    subject: string;
    topic: string;
    banca: string;
    year: string;
    institution?: string;
    status: string;
    keyword: string;
  };
  onFilterChange: (key: string, value: string) => void;
  onClearFilters: () => void;
  onStartFlashcards?: () => void;
  availableSubjects: string[];
  availableTopics: string[];
  availableBancas: string[];
  availableYears: string[];
  availableInstitutions?: string[];
  totalQuestions: number;
  loading?: boolean;
}

type OpenMenu = 'subject' | 'topic' | 'banca' | 'year' | null;

const normalizeSearchText = (value: string) =>
  (value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

const BANCA_CATALOG = [
  { sigla: 'CESPE', aliases: ['cebraspe', 'cespe', 'cespe/unb'] },
  { sigla: 'FCC', aliases: ['fundacao carlos chagas', 'fcc'] },
  { sigla: 'FGV', aliases: ['fundacao getulio vargas', 'fgv'] },
  { sigla: 'VUNESP', aliases: ['vunesp', 'fundacao vunesp'] },
  { sigla: 'IBFC', aliases: ['ibfc'] },
  { sigla: 'IADES', aliases: ['iades'] },
  { sigla: 'AOCP', aliases: ['aocp'] },
  { sigla: 'QUADRIX', aliases: ['quadrix'] },
  { sigla: 'CESGRANRIO', aliases: ['cesgranrio'] },
  { sigla: 'CONSULPLAN', aliases: ['consulplan'] },
  { sigla: 'FUNDATEC', aliases: ['fundatec'] },
  { sigla: 'IDECAN', aliases: ['idecan'] },
  { sigla: 'IDIB', aliases: ['idib'] },
  { sigla: 'FADESP', aliases: ['fadesp'] },
  { sigla: 'FAPEC', aliases: ['fapec'] },
  { sigla: 'FAPIPA', aliases: ['fapipa'] },
  { sigla: 'IBADE', aliases: ['ibade'] },
  { sigla: 'INAZ', aliases: ['inaz', 'inaz do para'] },
  { sigla: 'IBGP', aliases: ['ibgp'] },
  { sigla: 'OBJETIVA', aliases: ['objetiva'] },
  { sigla: 'LEGATUS', aliases: ['legatus'] },
  { sigla: 'UFRGS', aliases: ['ufrgs'] },
  { sigla: 'UFPR', aliases: ['ufpr'] },
  { sigla: 'FEPESE', aliases: ['fepese'] },
  { sigla: 'FUNRIO', aliases: ['funrio'] },
  { sigla: 'COPEVE', aliases: ['copeve'] },
  { sigla: 'CAIP', aliases: ['caip'] },
];

const isLikelySigla = (value: string) => /^[A-Z0-9]{2,12}$/.test(value.trim());

const inferSiglaFromName = (value: string) => {
  const norm = normalizeSearchText(value);
  const found = BANCA_CATALOG.find((entry) =>
    entry.aliases.some((alias) => norm.includes(alias) || alias.includes(norm)),
  );
  return found?.sigla;
};

const formatBancaLabel = (raw: string) => {
  const value = (raw || '').trim();
  if (!value || value === 'Todas') return 'Todas as bancas';

  const pairMatch = value.match(/^(.+?)\s*[-–]\s*(.+)$/);
  if (pairMatch) {
    const left = pairMatch[1].trim();
    const right = pairMatch[2].trim();

    if (isLikelySigla(left)) return `${left.toUpperCase()} · ${right}`;
    if (isLikelySigla(right)) return `${right.toUpperCase()} · ${left}`;
  }

  const parenthesesSigla = value.match(/\(([A-Z0-9]{2,12})\)\s*$/);
  if (parenthesesSigla) {
    const sigla = parenthesesSigla[1].toUpperCase();
    const name = value.replace(/\([A-Z0-9]{2,12}\)\s*$/, '').trim();
    return `${sigla} · ${name}`;
  }

  const upper = value.toUpperCase();
  const catalogBySigla = BANCA_CATALOG.find((entry) => entry.sigla === upper);
  if (catalogBySigla) return upper;

  const siglaFromName = inferSiglaFromName(value);
  if (siglaFromName) return `${siglaFromName} · ${value}`;

  if (value.length <= 10 && upper === value) return value;
  return value;
};

const summarizeSelected = (items: string[], formatter?: (value: string) => string) => {
  if (items.length === 0) return '';
  const shown = items.slice(0, 2).map((x) => (formatter ? formatter(x) : x));
  if (items.length <= 2) return shown.join(', ');
  return `${shown.join(', ')} +${items.length - 2}`;
};

const FilterPanel: React.FC<FilterPanelProps> = ({
  filters,
  onFilterChange,
  onClearFilters,
  onStartFlashcards,
  availableSubjects,
  availableTopics,
  availableBancas,
  availableYears,
  availableInstitutions = [], // mantido por compatibilidade mas não exibido
  totalQuestions,
  loading = false,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [searchFocused, setSearchFocused] = useState(false);
  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);

  const [subjectQuery, setSubjectQuery] = useState('');
  const [topicQuery, setTopicQuery] = useState('');
  const [bancaQuery, setBancaQuery] = useState('');

  const selectedBancas = useMemo(
    () => (filters.banca !== 'Todas' ? filters.banca.split(',').map((s) => s.trim()).filter(Boolean) : []),
    [filters.banca],
  );

  const selectedYears = useMemo(
    () => (filters.year !== 'Todos' ? filters.year.split(',').map((s) => s.trim()).filter(Boolean) : []),
    [filters.year],
  );

  const fallbackYears = useMemo(() => {
    const list: string[] = [];
    for (let y = 2026; y >= 1995; y--) list.push(String(y));
    return list;
  }, []);

  const yearsOptions = useMemo(() => {
    const cleaned = (availableYears || [])
      .map((y) => String(y).trim())
      .filter((y) => y && y !== 'Todos');
    return cleaned.length > 0 ? cleaned : fallbackYears;
  }, [availableYears, fallbackYears]);

  const filteredSubjects = useMemo(() => {
    const list = availableSubjects.filter((s) => s !== 'Todas');
    const q = normalizeSearchText(subjectQuery);
    return q ? list.filter((s) => normalizeSearchText(s).includes(q)) : list;
  }, [availableSubjects, subjectQuery]);

  const filteredTopics = useMemo(() => {
    const list = availableTopics.filter((t) => t !== 'Todos');
    const q = normalizeSearchText(topicQuery);
    return q ? list.filter((t) => normalizeSearchText(t).includes(q)) : list;
  }, [availableTopics, topicQuery]);


  const filteredBancas = useMemo(() => {
    const list = availableBancas.filter((b) => b !== 'Todas');
    const q = normalizeSearchText(bancaQuery);
    if (!q) return list;

    return list.filter((banca) => {
      const raw = normalizeSearchText(banca);
      const label = normalizeSearchText(formatBancaLabel(banca));
      const sigla = normalizeSearchText(inferSiglaFromName(banca) || '');
      return raw.includes(q) || label.includes(q) || sigla.includes(q);
    });
  }, [availableBancas, bancaQuery]);

  const hasActiveFilters =
    filters.subject !== 'Todas' ||
    filters.topic !== 'Todos' ||
    filters.banca !== 'Todas' ||
    filters.year !== 'Todos' ||
    filters.status !== 'Todas' ||
    filters.keyword !== '';

  const closeAll = () => {
    setOpenMenu(null);
    setSubjectQuery('');
    setTopicQuery('');
    setBancaQuery('');
  };

  useEffect(() => {
    const onMouseDown = (event: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(event.target as Node)) closeAll();
    };

    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeAll();
    };

    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onEscape);
    };
  }, []);

  return (
    <div ref={rootRef} className="w-full bg-white/95 rounded-3xl shadow-md border border-gray-100 mb-6 overflow-visible">
      <div className="px-5 md:px-6 pt-5 pb-4 border-b border-gray-100 flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center shadow-sm">
              <span className="text-white text-lg">🔍</span>
            </div>
            <div>
              <h2 className="font-bold text-gray-900">O que você quer estudar?</h2>
              <p className="text-sm text-gray-600">{loading ? 'Buscando…' : `${totalQuestions.toLocaleString('pt-BR')} questões`}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {onStartFlashcards && (
              <button
                onClick={onStartFlashcards}
                className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 text-sm font-semibold bg-amber-100 text-amber-800 border border-amber-200 rounded-lg hover:bg-amber-200 transition"
              >
                ⚡ Flashcards
              </button>
            )}
            {hasActiveFilters && (
              <button
                onClick={() => {
                  onClearFilters();
                  closeAll();
                }}
                className="px-3 py-1.5 text-sm font-semibold text-red-500 hover:bg-red-50 rounded-lg transition"
              >
                Limpar
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className={`flex-1 relative ${searchFocused ? 'ring-2 ring-emerald-500 rounded-xl' : ''}`}>
            <input
              type="text"
              value={filters.keyword}
              onChange={(e) => onFilterChange('keyword', e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              placeholder="Buscar por palavra-chave no enunciado..."
              className="w-full bg-gray-50 border-none rounded-xl pl-11 pr-10 py-3 text-sm focus:ring-0 focus:bg-gray-100 transition text-gray-900 placeholder-gray-400"
            />
            <svg className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {filters.keyword && (
              <button
                onClick={() => onFilterChange('keyword', '')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-200 transition"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="px-5 md:px-6 pb-6 pt-4 border-t border-gray-100 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-12 gap-4 items-end">
          {/* Matéria */}
          <div className="relative xl:col-span-3">
            <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-wider">Matéria</label>
            <button
              type="button"
              disabled={loading}
              onClick={() => setOpenMenu(openMenu === 'subject' ? null : 'subject')}
              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 flex items-center justify-between focus:ring-2 focus:ring-emerald-500 transition disabled:opacity-50"
            >
              <span className="truncate text-left">{filters.subject === 'Todas' ? 'Todas as matérias' : filters.subject}</span>
              <svg className={`w-4 h-4 text-gray-500 transition ${openMenu === 'subject' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {openMenu === 'subject' && (
              <div className="absolute left-0 right-0 z-50 mt-2 w-auto max-w-[calc(100vw-2rem)] md:left-auto md:right-0 md:w-full md:min-w-[340px] md:max-w-[640px] bg-white border border-gray-200 rounded-xl shadow-lg max-h-80 overflow-auto p-2">
                <input
                  type="text"
                  value={subjectQuery}
                  onChange={(e) => setSubjectQuery(e.target.value)}
                  placeholder="Digite a matéria"
                  className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm mb-2"
                />
                <button type="button" onClick={() => { onFilterChange('subject', 'Todas'); setOpenMenu(null); }} className="w-full text-left px-2 py-2 rounded-lg hover:bg-gray-50 text-sm">Todas as matérias</button>
                {filteredSubjects.map((subject) => (
                  <button
                    type="button"
                    key={subject}
                    onClick={() => { onFilterChange('subject', subject); setOpenMenu(null); }}
                    className={`w-full text-left px-2 py-2 rounded-lg text-sm ${filters.subject === subject ? 'bg-emerald-50 text-emerald-700 font-semibold' : 'hover:bg-gray-50 text-gray-800'}`}
                  >
                    {subject}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Assunto */}
          <div className="relative xl:col-span-3">
            <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-wider">Assunto</label>
            <button
              type="button"
              disabled={filters.subject === 'Todas' || loading}
              onClick={() => filters.subject !== 'Todas' && setOpenMenu(openMenu === 'topic' ? null : 'topic')}
              className={`w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 flex items-center justify-between focus:ring-2 focus:ring-emerald-500 transition ${filters.subject === 'Todas' ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span className="truncate text-left">{filters.topic === 'Todos' ? 'Todos os assuntos' : filters.topic}</span>
              <svg className={`w-4 h-4 text-gray-500 transition ${openMenu === 'topic' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {openMenu === 'topic' && (
              <div className="absolute left-0 right-0 z-50 mt-2 w-auto max-w-[calc(100vw-2rem)] md:left-auto md:right-0 md:w-full md:min-w-[340px] md:max-w-[640px] bg-white border border-gray-200 rounded-xl shadow-lg max-h-80 overflow-auto p-2">
                <input
                  type="text"
                  value={topicQuery}
                  onChange={(e) => setTopicQuery(e.target.value)}
                  placeholder="Digite o assunto"
                  className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm mb-2"
                />
                <button type="button" onClick={() => { onFilterChange('topic', 'Todos'); setOpenMenu(null); }} className="w-full text-left px-2 py-2 rounded-lg hover:bg-gray-50 text-sm">Todos os assuntos</button>
                {filteredTopics.map((topic) => (
                  <button
                    type="button"
                    key={topic}
                    onClick={() => { onFilterChange('topic', topic); setOpenMenu(null); }}
                    className={`w-full text-left px-2 py-2 rounded-lg text-sm ${filters.topic === topic ? 'bg-emerald-50 text-emerald-700 font-semibold' : 'hover:bg-gray-50 text-gray-800'}`}
                  >
                    {topic}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Banca */}
          <div className="relative xl:col-span-6">
            <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-wider">Banca</label>
            <button
              type="button"
              onClick={() => setOpenMenu(openMenu === 'banca' ? null : 'banca')}
              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 flex items-center justify-between focus:ring-2 focus:ring-emerald-500 transition"
            >
              <span className="truncate text-left">{selectedBancas.length ? summarizeSelected(selectedBancas, formatBancaLabel) : 'Todas as bancas'}</span>
              <svg className={`w-4 h-4 text-gray-500 transition ${openMenu === 'banca' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {openMenu === 'banca' && (
              <div className="absolute left-0 right-0 z-50 mt-2 w-auto max-w-[calc(100vw-2rem)] md:left-auto md:right-0 md:w-full md:min-w-[360px] md:max-w-[700px] bg-white border border-gray-200 rounded-xl shadow-lg max-h-80 overflow-auto p-2">
                <input
                  type="text"
                  value={bancaQuery}
                  onChange={(e) => setBancaQuery(e.target.value)}
                  placeholder="Digite a banca (ex: FGV)"
                  className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm mb-2"
                />

                <label className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer text-sm text-gray-800">
                  <input type="checkbox" className="rounded text-emerald-600" checked={selectedBancas.length === 0} onChange={() => onFilterChange('banca', 'Todas')} />
                  <span>Todas as bancas</span>
                </label>

                {filteredBancas.length === 0 ? (
                  <div className="px-2 py-3 text-sm text-gray-500">Nenhuma banca encontrada.</div>
                ) : (
                  filteredBancas.map((banca) => {
                    const active = selectedBancas.includes(banca);
                    return (
                      <label key={banca} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer text-sm text-gray-800">
                        <input
                          type="checkbox"
                          className="rounded text-emerald-600"
                          checked={active}
                          onChange={() => {
                            const next = active ? selectedBancas.filter((b) => b !== banca) : [...selectedBancas, banca].slice(0, 5);
                            onFilterChange('banca', next.length ? next.join(',') : 'Todas');
                          }}
                        />
                        <span className="truncate">{formatBancaLabel(banca)}</span>
                      </label>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-12 gap-4 items-end">
          {/* Ano */}
          <div className="relative xl:col-span-12">
            <label className="block text-xs font-bold text-gray-400 uppercase mb-2 tracking-wider">Ano</label>
            <button
              type="button"
              onClick={() => setOpenMenu(openMenu === 'year' ? null : 'year')}
              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 flex items-center justify-between focus:ring-2 focus:ring-emerald-500 transition"
            >
              <span className="truncate text-left">{selectedYears.length ? summarizeSelected(selectedYears) : 'Todos os anos'}</span>
              <svg className={`w-4 h-4 text-gray-500 transition ${openMenu === 'year' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {openMenu === 'year' && (
              <div className="absolute left-0 right-0 z-50 mt-2 w-auto max-w-[calc(100vw-2rem)] md:left-auto md:right-0 md:w-full md:min-w-[340px] md:max-w-[640px] bg-white border border-gray-200 rounded-xl shadow-lg max-h-72 overflow-auto p-2">
                {yearsOptions
                  .sort((a, b) => parseInt(b) - parseInt(a))
                  .map((year) => {
                    const active = selectedYears.includes(year);
                    return (
                      <label key={year} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer text-sm text-gray-800">
                        <input
                          type="checkbox"
                          className="rounded text-emerald-600"
                          checked={active}
                          onChange={() => {
                            const next = active ? selectedYears.filter((y) => y !== year) : [...selectedYears, year].slice(0, 10);
                            onFilterChange('year', next.length ? next.join(',') : 'Todos');
                          }}
                        />
                        <span>{year}</span>
                      </label>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;
